import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Settings, Trash2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { LoadingSpinner } from "../../../components/ui/loadingSpinner/loadingSpinner";
import {
  useFieldGroup,
  useFieldGroupInstances,
  useDeleteFieldGroupInstance,
  useDeleteFieldGroup,
} from "../../../hooks/useLibraries";
import { useFieldGroupsContext } from "../../../contexts/fieldGroupsContext";
import { useDebounce } from "../../../hooks/useDebounce";
import { useAuth } from "../../../contexts/authContext";
import { LibraryTable } from "./components/LibraryTable";
import { LibraryToolbar } from "./components/LibraryToolbar";
import { AddLibraryItemModal } from "./components/AddLibraryItemModal";
import { LibrarySettingsModal } from "./components/LibrarySettingsModal";
import { LibraryItemDrawer } from "./components/LibraryItemDrawer";
import { DeleteLibraryModal } from "./components/DeleteLibraryModal";
import { DeleteLibraryItemModal } from "./components/DeleteLibraryItemModal";
import { FieldGroupInstance } from "../../../models/audit/library";
import { hasPermission } from "../../../lib/permissions";

export const LibraryListPage = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { workspaceMember } = useAuth();

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [sortBy, setSortBy] = useState<string>("slug");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [editingInstance, setEditingInstance] =
    useState<FieldGroupInstance | null>(null);
  const [deletingInstance, setDeletingInstance] =
    useState<FieldGroupInstance | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const instanceIdFromUrl = searchParams.get("instance");
  const isDrawerOpen = Boolean(instanceIdFromUrl);

  const {
    fieldGroup,
    loading: loadingFieldGroup,
    refetch: refetchFieldGroup,
  } = useFieldGroup(groupId || null);

  const filters = useMemo(
    () => ({
      searchTerm: debouncedSearchTerm || undefined,
      sortBy: sortBy || undefined,
      sortOrder: sortOrder,
    }),
    [debouncedSearchTerm, sortBy, sortOrder],
  );

  const {
    instances,
    totalCount,
    loading: loadingInstances,
    loadMore,
    hasMore,
    refetch,
  } = useFieldGroupInstances(groupId || null, filters);

  const { deleteInstance, loading: loadingDelete } =
    useDeleteFieldGroupInstance();

  const { deleteFieldGroup, loading: loadingDeleteGroup } =
    useDeleteFieldGroup();
  const { refetch: refetchFieldGroups } = useFieldGroupsContext();

  const selectedInstance = useMemo(() => {
    if (!instanceIdFromUrl) return null;
    return instances.find((inst) => inst.id === instanceIdFromUrl) ?? null;
  }, [instanceIdFromUrl, instances]);

  useEffect(() => {
    if (!loadingInstances || instances.length > 0) {
      setIsInitialLoad(false);
    }
  }, [loadingInstances, instances.length]);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastInstanceRef = useCallback(
    (node: HTMLTableRowElement | null) => {
      if (loadingInstances) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      });

      if (node) observer.current.observe(node);
    },
    [loadingInstances, hasMore, loadMore],
  );

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const handleAddItem = () => {
    setEditingInstance(null);
    setIsModalOpen(true);
  };

  const handleViewItem = (instance: FieldGroupInstance) => {
    setSearchParams({ instance: instance.id }, { replace: true });
  };

  const handleDrawerOpenChange = (open: boolean) => {
    if (!open) {
      setSearchParams({}, { replace: true });
    }
  };

  const handleEditItem = (instance: FieldGroupInstance) => {
    setSearchParams({}, { replace: true });
    setEditingInstance(instance);
    setIsModalOpen(true);
  };

  const handleDeleteItem = (instance: FieldGroupInstance) => {
    setDeletingInstance(instance);
  };

  const handleConfirmDeleteItem = async () => {
    if (!deletingInstance) return;
    const success = await deleteInstance(deletingInstance.id);
    if (success) {
      setDeletingInstance(null);
      refetch();
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingInstance(null);
  };

  const handleSaveSuccess = () => {
    handleModalClose();
    refetch();
  };

  const handleDeleteLibrary = async () => {
    if (!groupId) return;
    const success = await deleteFieldGroup(groupId);
    if (success) {
      await refetchFieldGroups();
      navigate("/audit/controls/v2");
    }
  };

  if (!groupId) {
    navigate("/audit/controls/v2");
    return null;
  }

  if (loadingFieldGroup && !fieldGroup) {
    return (
      <div className="flex items-center justify-center min-h-[400px] w-full">
        <LoadingSpinner />
      </div>
    );
  }

  if (!loadingFieldGroup && !fieldGroup) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] w-full gap-4">
        <p className="text-stone-600">Library not found</p>
        <Button
          variant="outline"
          onClick={() => navigate("/audit/controls/v2")}
        >
          Back to Controls
        </Button>
      </div>
    );
  }

  const canEdit = hasPermission(workspaceMember, "edit_control");

  return (
    <div className="h-full mx-auto w-full max-w-6xl p-6 flex flex-col overflow-hidden">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {fieldGroup?.name || "Library"}
          </h1>
          {fieldGroup?.description && (
            <p className="text-stone-600">{fieldGroup.description}</p>
          )}
        </div>
        <div className="flex items-center gap-4">
          {canEdit && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsSettingsOpen(true)}
                className="h-8"
              >
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsDeleteOpen(true)}
                data-testid="library-delete-button"
                className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 hover:border-red-300"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
          <div className="text-sm text-stone-600">
            {instances.length} of {totalCount} items
          </div>
        </div>
      </div>

      <LibraryToolbar
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        onAddItem={handleAddItem}
        canEdit={canEdit}
      />

      {loadingInstances && isInitialLoad ? (
        <div className="flex items-center justify-center flex-1">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="overflow-y-auto overflow-x-auto flex-1">
          <LibraryTable
            instances={instances}
            fieldGroup={fieldGroup}
            onInstanceClick={handleViewItem}
            onDeleteInstance={handleDeleteItem}
            lastInstanceRef={lastInstanceRef}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            loading={loadingInstances}
            loadingDelete={loadingDelete}
            canEdit={canEdit}
          />
        </div>
      )}

      {fieldGroup && (
        <>
          <AddLibraryItemModal
            open={isModalOpen}
            onOpenChange={handleModalClose}
            fieldGroup={fieldGroup}
            editingInstance={editingInstance}
            onSuccess={handleSaveSuccess}
          />
          <LibrarySettingsModal
            open={isSettingsOpen}
            onOpenChange={setIsSettingsOpen}
            fieldGroup={fieldGroup}
            onSuccess={refetchFieldGroup}
          />
          <LibraryItemDrawer
            open={isDrawerOpen}
            onOpenChange={handleDrawerOpenChange}
            instance={selectedInstance}
            fieldGroup={fieldGroup}
            onEdit={handleEditItem}
            canEdit={canEdit}
          />
          <DeleteLibraryModal
            open={isDeleteOpen}
            onOpenChange={setIsDeleteOpen}
            fieldGroup={fieldGroup}
            onConfirm={handleDeleteLibrary}
            loading={loadingDeleteGroup}
          />
          <DeleteLibraryItemModal
            open={Boolean(deletingInstance)}
            onOpenChange={(open) => !open && setDeletingInstance(null)}
            instance={deletingInstance}
            onConfirm={handleConfirmDeleteItem}
            loading={loadingDelete}
          />
        </>
      )}
    </div>
  );
};
